// src/api/apiClient.js
import axios from "axios";
import keycloak from "../keycloak";
import config from "../config";
import { getActiveSupportViewAs } from "../supportViewAs/SupportViewAsProvider";

const baseURL = (config?.apiBaseUrl || process.env.REACT_APP_API_BASE_URL || "http://localhost:8080/api/v1")
  .replace(/\/+$/, "");
const integrationBaseURL = (
  config?.integrationApiBaseUrl ||
  process.env.REACT_APP_NOCTIS_API_BASE_URL ||
  process.env.REACT_APP_INTEGRATION_API_BASE_URL ||
  baseURL.replace(/\/v1\/?$/, "")
).replace(/\/+$/, "");
const parseDebugFlag = (value) => {
  if (typeof value === "boolean") return value;
  if (value == null) return null;
  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return null;
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return null;
};
const resolvedDebugFlag =
  parseDebugFlag(config?.apiDebug) ??
  parseDebugFlag(process.env.REACT_APP_API_DEBUG) ??
  (process.env.NODE_ENV === "development");
// Never log request/response payloads in production builds.
const debugEnabled = process.env.NODE_ENV === "development" && Boolean(resolvedDebugFlag);
const debugLog = (...args) => {
  if (!debugEnabled) return;
  console.debug(...args);
};

const isLocalDev = () => {
  if (process.env.NODE_ENV === "development") return true;
  try {
    if (typeof window === "undefined") return false;
    const host = window.location?.hostname || "";
    return host === "localhost" || host === "127.0.0.1" || host === "::1";
  } catch {
    return false;
  }
};

const safeAtob = (value) => {
  if (!value) return null;
  try {
    if (typeof window !== "undefined" && typeof window.atob === "function") return window.atob(value);
  } catch {
    // ignore
  }
  try {
    // eslint-disable-next-line no-undef
    if (typeof atob === "function") return atob(value);
  } catch {
    // ignore
  }
  return null;
};

const emitDryRunEvents = (events, source = "header") => {
  if (!isLocalDev()) return;
  if (!Array.isArray(events) || events.length === 0) return;
  try {
    if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
    const seen = new Set();
    events.forEach((ev) => {
      const key = JSON.stringify(ev ?? {});
      if (seen.has(key)) return;
      seen.add(key);
      window.dispatchEvent(
        new CustomEvent("lumen:toast", {
          detail: {
            kind: "dry-run",
            source,
            event: ev,
          },
        })
      );
    });
  } catch {
    // ignore
  }
};

const extractDryRunEventsFromResponse = (res) => {
  if (!isLocalDev()) return null;
  if (!res) return null;

  const events = [];

  try {
    const headers = res?.headers || {};
    const headerValue =
      headers["x-lumen-dry-run-events"] ||
      headers["X-Lumen-Dry-Run-Events"] ||
      null;
    if (typeof headerValue === "string" && headerValue.trim()) {
      const decoded = safeAtob(headerValue.trim());
      if (decoded) {
        const parsed = JSON.parse(decoded);
        if (Array.isArray(parsed)) events.push(...parsed);
      }
    }
  } catch {
    // ignore
  }

  try {
    const bodyEvents = res?.data?.dryRunEvents;
    if (Array.isArray(bodyEvents)) events.push(...bodyEvents);
  } catch {
    // ignore
  }

  if (events.length === 0) return null;
  return events;
};

const attachInterceptors = (instance, label = "api") => {
  instance.interceptors.request.use(
    async (req) => {
      try {
        if (keycloak?.updateToken) await keycloak.updateToken(30);
      } catch (e) {
        debugLog(`[${label}] updateToken failed:`, e?.message || e);
      }
      if (keycloak?.token) {
        req.headers = req.headers || {};
        req.headers.Authorization = `Bearer ${keycloak.token}`;
      }
      try {
        const viewAs = getActiveSupportViewAs();
        const target = viewAs?.active ? viewAs.target : null;
        if (target?.userId || target?.email) {
          const method = String(req.method || "get").trim().toLowerCase();
          if (!["get", "head", "options"].includes(method)) {
            const err = new Error("Support view-as is read-only.");
            err.code = "LUMEN_VIEW_AS_READ_ONLY";
            err.config = req;
            return Promise.reject(err);
          }
          req.headers = req.headers || {};
          req.headers["X-Lumen-View-As"] = "true";
          req.headers["X-Lumen-View-As-Read-Only"] = "true";
          if (target.userId) req.headers["X-Lumen-View-As-User"] = target.userId;
          if (target.email) req.headers["X-Lumen-View-As-Email"] = target.email;
          if (target.issueNumber) req.headers["X-Lumen-View-As-Issue"] = target.issueNumber;
          if (target.source) req.headers["X-Lumen-View-As-Source"] = target.source;
        }
      } catch (e) {
        debugLog(`[${label}] support view-as headers failed:`, e?.message || e);
      }
      try {
        if (typeof window !== "undefined") {
          req.headers = req.headers || {};
          const pageId = window.__LUMEN_PAGE_ID__ || null;
          const featureId = window.__LUMEN_FEATURE_ID__ || null;
          if (!req.headers["X-Lumen-Page"]) req.headers["X-Lumen-Page"] = pageId || undefined;
          if (!req.headers["X-Lumen-Feature"]) req.headers["X-Lumen-Feature"] = featureId || undefined;
        }
      } catch (e) {
        debugLog(`[${label}] analytics headers failed:`, e?.message || e);
      }
      debugLog(`[${label}] →`, req.method?.toUpperCase(), req.baseURL + req.url, {
        headers: req.headers,
      });
      return req;
    },
    (err) => {
      debugLog(`[${label}] request error:`, err);
      return Promise.reject(err);
    }
  );

  instance.interceptors.response.use(
    (res) => {
      try {
        const events = extractDryRunEventsFromResponse(res);
        if (events?.length) emitDryRunEvents(events, "response");
      } catch (e) {
        debugLog(`[${label}] dry-run events decode failed:`, e?.message || e);
      }
      debugLog(`[${label}] ←`, res.status, res.config?.url, res.data);
      return res;
    },
    (err) => {
      if (err?.response) {
        debugLog(`[${label}] ←`, err.response.status, err.config?.url, err.response?.data);
      } else {
        debugLog(`[${label}] network/unknown error:`, err?.message || err);
      }
      return Promise.reject(err);
    }
  );

  return instance;
};

const api = attachInterceptors(
  axios.create({
    baseURL,
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  }),
  "api"
);

const integrationApi = attachInterceptors(
  axios.create({
    baseURL: integrationBaseURL,
    withCredentials: false,
    headers: { "Content-Type": "application/json" },
  }),
  "integration"
);

export default api;
export { integrationApi };
